import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import api from '../services/api'
import { useIsMobile } from '../hooks/useIsMobile'
import toast from 'react-hot-toast'
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Filter,
  Gavel,
  Globe2,
  Layers,
  Pause,
  Play,
  PowerOff,
  RefreshCw,
  Send,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  X,
  XCircle,
  Zap,
} from 'lucide-react'
import { format, parseISO, formatDistanceToNow } from 'date-fns'

// ─────────────────────────────────────────────────────────────────────────────
//   WATERFALL VALIDATOR — Panel de validación de la rotación y la subasta
//   Endpoints consumidos:
//     GET /core/waterfall/leads                    (paginado)
//     GET /core/waterfall/leads/{kickoff_lead_id}
//     GET /core/waterfall/rotation/stats
//     GET /core/waterfall/health
//     GET /core/waterfall/lenders
//     GET /core/waterfall/engine/leads             (vista paralela Engine)
// ─────────────────────────────────────────────────────────────────────────────

const POLL_LEADS_MS   = 5000
const POLL_STATS_MS   = 5000
const POLL_SUMMARY_MS = 5000
const POLL_HEALTH_MS  = 15000
const PAGE_SIZE       = 100
function getDateRangeSinceIso(dateRange) {
  const now = new Date()
  if (dateRange === 'today') {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
  }
  if (dateRange === '7d') {
    return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString()
  }
  if (dateRange === '30d') {
    return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString()
  }
  if (dateRange === 'ytd') {
    return new Date(now.getFullYear(), 0, 1).toISOString()
  }
  return null
}

function DateRangeSelector({ value, onChange }) {
  const options = [
    { id: 'today', label: 'Today' },
    { id: '7d',    label: '7 Days' },
    { id: '30d',   label: '30 Days' },
    { id: 'ytd',   label: 'YTD' },
  ]
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', background: 'var(--bg-raised)',
      padding: 3, borderRadius: 10, border: '1px solid var(--border)', gap: 2
    }}>
      {options.map(opt => (
        <button
          key={opt.id} type="button" onClick={() => onChange(opt.id)}
          style={{
            padding: '5px 12px', fontSize: 12, fontWeight: value === opt.id ? 700 : 500,
            borderRadius: 7, border: 'none', cursor: 'pointer',
            background: value === opt.id ? 'var(--accent)' : 'transparent',
            color: value === opt.id ? '#ffffff' : 'var(--text-secondary)',
            transition: 'all 0.15s ease',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// Paleta consistente por lender. Si aparecen más, fallback gris.
const LENDER_COLORS = {
  highwinds:   { color: 'var(--amber)',  bg: 'var(--amber-bg)',  letter: 'H' },
  teapot:      { color: 'var(--blue)',   bg: 'var(--blue-bg)',   letter: 'T' },
  leadstack:   { color: 'var(--green)',  bg: 'var(--green-bg)',  letter: 'L' },
  creditninja: { color: 'var(--red)',    bg: 'var(--red-bg)',    letter: 'C' },
  hyalite:     { color: 'var(--purple)', bg: 'var(--purple-bg)', letter: 'Y' },
  engine:      { color: '#7C3AED',       bg: 'rgba(124,58,237,0.15)', letter: 'E' },
}
const getLenderStyle = (identifier) => {
  if (!identifier) return { color: 'var(--text-muted)', bg: 'var(--bg-raised)', letter: '?' }
  return LENDER_COLORS[identifier] || {
    color: 'var(--text-muted)',
    bg: 'var(--bg-raised)',
    letter: identifier[0]?.toUpperCase() || '?',
  }
}

// HTTP code → color semántico
const httpColor = (code) => {
  if (!code) return 'var(--text-muted)'
  if (code >= 200 && code < 300) return 'var(--green)'
  if (code === 408) return 'var(--amber)'
  if (code >= 400 && code < 500) return 'var(--red)'
  if (code >= 500) return 'var(--red)'
  return 'var(--text-muted)'
}

// State → label, color, bg
// Punto 0: "ventana" → "rotación" en toda la UI.
const STATE_META = {
  intake:        { label: 'INTAKE',              color: 'var(--text-muted)', bg: 'var(--bg-raised)' },
  exclusive:     { label: 'IN ROTATION',         color: 'var(--amber)',      bg: 'var(--amber-bg)'  },
  auction:       { label: 'IN AUCTION',          color: 'var(--purple)',     bg: 'var(--purple-bg)' },
  won_exclusive: { label: 'ROTATION WINNER', color: 'var(--green)',      bg: 'var(--green-bg)'  },
  won_auction:   { label: 'AUCTION WINNER',  color: 'var(--blue)',       bg: 'var(--blue-bg)'   },
  rejected_all:  { label: 'REJECTED',           color: 'var(--red)',        bg: 'var(--red-bg)'    },
  error:         { label: 'ERROR',               color: 'var(--red)',        bg: 'var(--red-bg)'    },
}
const getStateMeta = (state) => STATE_META[state] || STATE_META.intake

// ─────────────────────────────────────────────────────────────────────────────
//   REGLA DE FILTRADO (Punto 1)
//   Combinación estado + lender → qué param mandar al backend.
//
//   Estados que apuntan a "fase rotación" (preferred_lender):
//     exclusive, won_exclusive, rejected_all, error
//   Estados que apuntan a "fase subasta" (winner_lender):
//     auction, won_auction
//   Sin estado seleccionado + lender → any_lender (OR de preferred/winner)
// ─────────────────────────────────────────────────────────────────────────────
const ROTATION_STATES = ['exclusive', 'won_exclusive', 'rejected_all', 'error']
const AUCTION_STATES  = ['auction', 'won_auction']

function resolveLenderParam(state, lender) {
  if (!lender) return null
  if (!state)  return { key: 'any_lender', value: lender }
  if (ROTATION_STATES.includes(state)) return { key: 'preferred_lender', value: lender }
  if (AUCTION_STATES.includes(state))  return { key: 'winner_lender',    value: lender }
  return { key: 'any_lender', value: lender }
}

// Helpers de formato
const fmtTime = (ts) => {
  if (!ts) return '—'
  try { return format(parseISO(ts), 'HH:mm:ss') } catch { return '—' }
}
const fmtDateTime = (ts) => {
  if (!ts) return '—'
  try { return format(parseISO(ts), 'dd/MM HH:mm:ss') } catch { return '—' }
}
const fmtMs = (ms) => {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(2)}s`
}
const fmtMoney = (n) => {
  if (n == null) return '—'
  return `$${Number(n).toFixed(2)}`
}
const fmtRelative = (ts) => {
  if (!ts) return '—'
  try { return formatDistanceToNow(parseISO(ts), { addSuffix: true }) } catch { return '—' }
}

// Reglas de Engine (deben coincidir con app/lenders/engine/config.py)
const ENGINE_MIN_INCOME = 20000
const ENGINE_SPLIT_PROBABILITY = 0.30

// Deriva httpCode + texto de tiempo de un lead (compartido entre fila y tarjeta).
function getLeadRowDerived(lead) {
  const final = lead.final || {}
  const exclusive = lead.exclusive || {}

  let httpCode = null
  if (final.winner_http_status_code != null) {
    httpCode = final.winner_http_status_code
  } else if (exclusive.http_status_code != null) {
    httpCode = exclusive.http_status_code
  }

  let timeDisplay = '—'
  if (lead.state === 'exclusive' && exclusive.expires_at) {
    const remaining = Math.max(0, Math.round((new Date(exclusive.expires_at).getTime() - Date.now()) / 1000))
    timeDisplay = `${remaining}s left`
  } else if (lead.state === 'auction') {
    const startedAt = lead.auction?.started_at
    if (startedAt) {
      const elapsed = Math.round((Date.now() - new Date(startedAt).getTime()) / 1000)
      timeDisplay = `+${elapsed}s auction`
    }
  } else if (final.resolved_at) {
    timeDisplay = fmtRelative(final.resolved_at)
  }

  return { httpCode, timeDisplay }
}

// ════════════════════════════════════════════════════════════════════════════
//   PANEL DE LENDERS (Punto 2)
// ════════════════════════════════════════════════════════════════════════════
function LendersPanel({ lenders, distribution }) {
  const isMobile = useIsMobile()

  // Map identifier → count en la ventana actual (para mostrar volumen)
  const countByIdentifier = useMemo(() => {
    const m = {}
    ;(distribution || []).forEach((d) => { m[d._id] = d.count })
    return m
  }, [distribution])

  if (!lenders || lenders.length === 0) {
    return null
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        padding: isMobile ? '12px 16px' : '14px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'linear-gradient(to right, var(--bg-surface), var(--bg-raised))',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 6 : 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Users size={16} style={{ color: 'var(--accent)' }} />
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Lenders</h3>
          {!isMobile && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              · on = participating in the waterfall
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          <span style={{ color: 'var(--green)', fontWeight: 700 }}>
            {lenders.filter(l => l.in_rotation).length}
          </span>
          {' active'}
          {lenders.some(l => l.is_fallback && l.is_active) && (
            <> · <span style={{ color: 'var(--purple)' }}>1 fallback</span></>
          )}
        </div>
      </div>

      <div style={{
        padding: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        flexWrap: 'wrap', gap: 10,
      }}>
        {lenders.map((l) => (
          <LenderChip
            key={l.identifier}
            lender={l}
            count={countByIdentifier[l.identifier] || 0}
            mobile={isMobile}
          />
        ))}
      </div>
    </div>
  )
}

function LenderChip({ lender, count, mobile }) {
  const style = getLenderStyle(lender.identifier)
  // "lit" = participa del waterfall ahora mismo (kill-switch ON y no es Hyalite)
  const lit = lender.in_rotation
  // Hyalite con código activo: lo mostramos atenuado pero con icono distintivo
  const isFallback = lender.is_fallback && lender.is_active

  return (
    <div
      title={`${lender.name || lender.identifier} · priority ${lender.priority ?? '—'}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px 8px 8px',
        borderRadius: 10,
        background: lit ? 'var(--bg-surface)' : 'var(--bg-raised)',
        border: `1px solid ${lit ? `${style.color}40` : 'var(--border)'}`,
        opacity: lit ? 1 : isFallback ? 0.75 : 0.5,
        minWidth: mobile ? 0 : 160,
        width: mobile ? '100%' : undefined,
        transition: 'all 0.2s',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: lit ? style.color : 'var(--bg-surface)',
        color: lit ? '#FFF' : 'var(--text-muted)',
        border: lit ? 'none' : `1px solid ${style.color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: 14,
        fontFamily: 'ui-monospace, monospace',
        flexShrink: 0,
      }}>
        {!lender.is_active && !isFallback
          ? <PowerOff size={14} />
          : style.letter}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: 'var(--text-primary)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {lender.name || lender.identifier}
        </div>
        {(lit && count > 0) || isFallback ? (
          <div style={{
            fontSize: 9, color: 'var(--text-muted)', marginTop: 2,
            fontFamily: 'ui-monospace, monospace',
          }}>
            {isFallback
              ? 'fallback'
              : `${count} lead${count !== 1 ? 's' : ''} · 60min`}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   COCKPIT — Estado del rotador en tiempo real
// ════════════════════════════════════════════════════════════════════════════
function RotationCockpit({ stats }) {
  const isMobile = useIsMobile()

  if (!stats) {
    return (
      <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1.4s linear infinite' }} />
        <p style={{ marginTop: 12, fontSize: 13 }}>Loading rotator status…</p>
      </div>
    )
  }

  const { distribution = [], recent_turns = [], current_counter = 0, next_turn_number } = stats
  const totalRecent = distribution.reduce((s, d) => s + d.count, 0)
  const expectedPct = distribution.length > 0 ? 100 / distribution.length : 0

  const lastTurn = recent_turns[recent_turns.length - 1]
  const nextLenderHint = lastTurn?.preferred_lender || null

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        padding: isMobile ? '14px 16px' : '16px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'linear-gradient(to right, var(--bg-surface), var(--bg-raised))',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Target size={16} style={{ color: 'var(--accent)' }} />
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Rotating counter</h3>
          <span className="badge badge-blue" style={{ fontSize: 10 }}>
            Counter #{current_counter}
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Rotation · last {stats.window_minutes} min
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '260px 1fr 320px',
        gap: isMobile ? 20 : 24,
        padding: isMobile ? 16 : 24,
        alignItems: isMobile ? 'stretch' : 'center',
      }}>
        {/* Siguiente turno */}
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
            color: 'var(--text-muted)', marginBottom: 12,
          }}>
            NEXT TURN
          </div>
          {nextLenderHint ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <LenderBadge identifier={nextLenderHint.identifier} size={52} />
              <div>
                <div style={{ fontSize: 17, fontWeight: 700 }}>
                  {nextLenderHint.name || nextLenderHint.identifier}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Turn #{next_turn_number}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Esperando primer lead…
            </div>
          )}
        </div>

        {/* Secuencia */}
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
            color: 'var(--text-muted)', marginBottom: 12,
            display: 'flex', justifyContent: 'space-between', gap: 8,
            flexWrap: 'wrap',
          }}>
            <span>SEQUENCE · LAST {recent_turns.length} TURNS</span>
            <span style={{ color: 'var(--text-secondary)' }}>
              {totalRecent} leads in rotation
            </span>
          </div>
          {recent_turns.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              No turns recorded yet
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {recent_turns.map((turn, idx) => {
                const isLatest = idx === recent_turns.length - 1
                const lender = turn.preferred_lender || {}
                const style = getLenderStyle(lender.identifier)
                return (
                  <div key={turn._id || idx} style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 3,
                    opacity: isLatest ? 1 : 0.55 + (idx / recent_turns.length) * 0.4,
                  }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 7,
                      background: style.color, color: '#FFF',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: 12,
                      fontFamily: 'ui-monospace, monospace',
                      boxShadow: isLatest ? `0 0 0 2px var(--bg-surface), 0 0 0 4px ${style.color}` : 'none',
                      transition: 'all 0.3s',
                    }}>
                      {style.letter}
                    </div>
                    <div style={{
                      fontSize: 9, color: 'var(--text-muted)',
                      fontFamily: 'ui-monospace, monospace',
                    }}>
                      #{turn.turn_number}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Distribución */}
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
            color: 'var(--text-muted)', marginBottom: 12,
            display: 'flex', justifyContent: 'space-between',
          }}>
            <span>DISTRIBUTION</span>
            {expectedPct > 0 && (
              <span style={{ color: 'var(--text-secondary)' }}>
                Ideal {expectedPct.toFixed(1)}%
              </span>
            )}
          </div>
          {distribution.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</div>
          ) : (
            <>
              <div style={{
                display: 'flex', height: 10, borderRadius: 4,
                overflow: 'hidden', background: 'var(--bg-raised)',
                marginBottom: 12,
              }}>
                {distribution.map((d) => {
                  const style = getLenderStyle(d._id)
                  return (
                    <div key={d._id} style={{
                      width: `${(d.count / totalRecent) * 100}%`,
                      background: style.color, transition: 'width 0.5s',
                    }} title={`${d._id}: ${d.count}`} />
                  )
                })}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {distribution.map((d) => {
                  const pct = (d.count / totalRecent) * 100
                  const drift = pct - expectedPct
                  const style = getLenderStyle(d._id)
                  const ok = Math.abs(drift) < 5
                  return (
                    <div key={d._id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 11,
                    }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: 2,
                        background: style.color, flexShrink: 0,
                      }} />
                      <span style={{ flex: 1, color: 'var(--text-secondary)' }}>
                        {d.lender_name || d._id}
                      </span>
                      <span style={{
                        fontFamily: 'ui-monospace, monospace', fontWeight: 700,
                      }}>
                        {pct.toFixed(1)}%
                      </span>
                      <span style={{
                        fontFamily: 'ui-monospace, monospace', fontSize: 10,
                        color: ok ? 'var(--green)' : 'var(--amber)',
                        minWidth: 36, textAlign: 'right',
                      }}>
                        {drift >= 0 ? '+' : ''}{drift.toFixed(1)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   STATS STRIP — dos filas:
//     Fila 1 (embudo global): Total leads · Total waterfall · Engine OK · Rechazados
//     Fila 2 (distribución del waterfall): los 6 estados + aceptación %
//
//   Los números vienen del endpoint /waterfall/summary, NO se calculan
//   contando lo visible en la página. Cada contador es una query
//   independiente a su colección (partnerLeads, waterfallLeadFlow,
//   leadlogs), sin aritmética derivada — así si hay drift entre
//   colecciones se ve.
//
//   El contador "Rechazados antes del waterfall" es clickeable: abre
//   un modal con la lista paginada de leads + motivo.
// ════════════════════════════════════════════════════════════════════════════
function StatsStrip({ summary, onOpenRejected, onOpenRejectedByLender, dateRange }) {
  const isMobile = useIsMobile()
  const loading = !summary

  // Fila 1 — embudo global
  // Las celdas S2S se generan dinámicamente desde summary.s2s_partners
  // para que sumar/quitar partners sea solo backend (modificar la
  // constante S2S_PARTNERS en waterfall.py).
  const s2sCells = Object.entries(summary?.s2s_partners || {}).map(
    ([affId, info]) => ({
      label: `S2S ${info.name}`,
      val: info.count,
      color: '#0EA5A4',  // cyan/teal — distinto del azul del total
      hint: `${info.name} sends S2S and does not enter the waterfall · aff_id=${affId}`,
    })
  )

  const funnel = [
    {
      label: 'Total leads',
      val: summary?.total_leads,
      color: 'var(--blue)',
      hint: 'partnerLeads in selected period',
    },
    ...s2sCells,
    {
      label: 'Total in waterfall',
      val: summary?.total_waterfall,
      color: 'var(--text-primary)',
      hint: 'leads that entered the rotator',
    },
    {
      label: 'Engine successful',
      val: summary?.engine_success,
      color: '#7C3AED',
      hint: 'won the 30/70 draw and Engine responded OK',
    },
    {
      label: 'Rejected',
      val: summary?.rejected_before_waterfall,
      color: 'var(--red)',
      hint: 'rejected before the waterfall · click for detail',
      clickable: true,
      onClick: onOpenRejected,
    },
  ]


  const byState = summary?.by_state || {}
  const distribution = [
    { label: 'In rotation',          val: byState.exclusive       || 0, color: 'var(--amber)'   },
    { label: 'In auction',           val: byState.auction         || 0, color: 'var(--purple)'  },
    { label: 'Rotation winner',  val: byState.won_exclusive   || 0, color: 'var(--green)'   },
    { label: 'Auction winner',   val: byState.won_auction     || 0, color: 'var(--blue)'    },
    {
      label: 'Rejected (did not buy)',
      val: byState.rejected_all || 0,
      color: 'var(--red)',
      clickable: true,
      onClick: onOpenRejectedByLender,
    },
  ]
  const acceptance = summary?.acceptance_pct ?? 0
  const rangeLabel = dateRange ? dateRange.toUpperCase() : 'TODAY'
  const onlyToday = !dateRange || dateRange === 'today'

  // ── Versión móvil: embudo y distribución en tarjetas 2×2 ──────────────────
  if (isMobile) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '10px 14px', background: 'var(--bg-raised)',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.2, color: 'var(--text-muted)' }}>
            GLOBAL FUNNEL
          </span>
          <span style={{
            fontSize: 9,
            color: onlyToday ? 'var(--accent)' : 'var(--text-muted)',
            fontWeight: onlyToday ? 700 : 500,
          }}>
            {rangeLabel}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '12px 14px' }}>
          {funnel.map((s) => (
            <FunnelCell
              key={s.label}
              mobile
              label={s.label}
              value={s.val}
              color={s.color}
              hint={s.hint}
              loading={loading}
              clickable={s.clickable}
              onClick={s.onClick}
            />
          ))}
        </div>

        <div style={{
          padding: '10px 14px', background: 'var(--bg-raised)',
          borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
        }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.2, color: 'var(--text-muted)' }}>
            WATERFALL DISTRIBUTION
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '12px 14px' }}>
          {distribution.map((s) => (
            <DistributionCell key={s.label} s={s} loading={loading} mobile />
          ))}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', background: 'var(--bg-raised)',
          borderTop: '1px solid var(--border)',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)' }}>
            <TrendingUp size={14} /> Rotation acceptance
          </span>
          <span style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 20, fontWeight: 800,
            opacity: loading ? 0.4 : 1,
            color: acceptance > 50 ? 'var(--green)'
                 : acceptance > 30 ? 'var(--amber)'
                 : 'var(--red)',
          }}>
            {loading ? '—' : `${acceptance}%`}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 0,
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {/* ── Fila 1: Embudo global ─────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 0, alignItems: 'stretch',
        background: 'var(--bg-raised)',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          padding: '10px 16px',
          fontSize: 9, fontWeight: 700, letterSpacing: 1.2,
          color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center',
          borderRight: '1px solid var(--border)',
          minWidth: 160,
        }}>
          GLOBAL FUNNEL
          <span style={{
            marginLeft: 8, fontSize: 9,
            color: onlyToday ? 'var(--accent)' : 'var(--text-muted)',
            opacity: onlyToday ? 1 : 0.7,
            fontWeight: onlyToday ? 700 : 500,
            letterSpacing: 0,
          }}>
            {rangeLabel}
          </span>
        </div>

        {funnel.map((s) => (
          <FunnelCell
            key={s.label}
            label={s.label}
            value={s.val}
            color={s.color}
            hint={s.hint}
            loading={loading}
            clickable={s.clickable}
            onClick={s.onClick}
          />
        ))}
      </div>

      {/* ── Fila 2: Distribución del waterfall ───────────────────── */}
      <div style={{
        display: 'flex', gap: 28, alignItems: 'center',
        padding: '14px 24px',
        flexWrap: 'wrap',
      }}>
        <div style={{
          fontSize: 9, fontWeight: 700, letterSpacing: 1.2,
          color: 'var(--text-muted)', minWidth: 160,
        }}>
          WATERFALL DISTRIBUTION
        </div>
        {distribution.map((s, i) => (
          <React.Fragment key={s.label}>
            <DistributionCell s={s} loading={loading} />
            {i < distribution.length - 1 && (
              <span style={{ color: 'var(--border-strong)', opacity: 0.4 }}>|</span>
            )}
          </React.Fragment>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <TrendingUp size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Rotation acceptance
          </span>
          <span style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 20, fontWeight: 800,
            opacity: loading ? 0.4 : 1,
            color: acceptance > 50 ? 'var(--green)'
                 : acceptance > 30 ? 'var(--amber)'
                 : 'var(--red)',
          }}>
            {loading ? '—' : `${acceptance}%`}
          </span>
        </div>
      </div>
    </div>
  )
}

function FunnelCell({ label, value, color, hint, loading, clickable, onClick, mobile }) {
  const [hover, setHover] = useState(false)

  if (mobile) {
    return (
      <div
        onClick={clickable ? onClick : undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={hint}
        style={{
          padding: '12px 14px',
          cursor: clickable ? 'pointer' : 'default',
          background: clickable && hover ? `${color}10` : 'var(--bg-raised)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          transition: 'background 0.15s',
        }}
      >
        <span style={{
          display: 'block',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 22, fontWeight: 800, color,
          opacity: loading ? 0.4 : 1,
        }}>
          {loading ? '—' : (value ?? 0).toLocaleString()}
        </span>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 6, marginTop: 2,
          fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600,
        }}>
          {label}
          {clickable && (
            <ExternalLink size={11} style={{ color: hover ? color : 'var(--text-muted)', opacity: hover ? 1 : 0.5 }} />
          )}
        </span>
      </div>
    )
  }

  return (
    <div
      onClick={clickable ? onClick : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={hint}
      style={{
        flex: 1,
        padding: '14px 18px',
        borderRight: '1px solid var(--border)',
        cursor: clickable ? 'pointer' : 'default',
        background: clickable && hover ? `${color}10` : 'transparent',
        transition: 'background 0.15s',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 24, fontWeight: 800, color,
          opacity: loading ? 0.4 : 1,
        }}>
          {loading ? '—' : (value ?? 0).toLocaleString()}
        </span>
        <span style={{
          fontSize: 11, color: 'var(--text-secondary)',
          fontWeight: 600,
        }}>
          {label}
        </span>
        {clickable && (
          <ExternalLink
            size={11}
            style={{
              color: hover ? color : 'var(--text-muted)',
              marginLeft: 'auto', opacity: hover ? 1 : 0.4,
              transition: 'all 0.15s',
            }}
          />
        )}
      </div>
      {hint && (
        <div style={{
          fontSize: 9, color: 'var(--text-muted)',
          marginTop: 4, fontStyle: 'italic',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {hint}
        </div>
      )}
    </div>
  )
}

function DistributionCell({ s, loading, mobile }) {
  const [hover, setHover] = useState(false)
  const clickable = !!s.clickable

  if (mobile) {
    return (
      <div
        onClick={clickable ? s.onClick : undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex', flexDirection: 'column', gap: 2,
          padding: '8px 10px', borderRadius: 8,
          border: '1px solid var(--border)',
          background: clickable && hover ? `${s.color}10` : 'var(--bg-raised)',
          cursor: clickable ? 'pointer' : 'default',
          transition: 'background 0.15s',
        }}
      >
        <span style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 20, fontWeight: 800, color: s.color,
          opacity: loading ? 0.4 : 1,
        }}>
          {loading ? '—' : s.val}
        </span>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 9, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
        }}>
          {s.label}
          {clickable && (
            <ExternalLink size={9} style={{ color: hover ? s.color : 'var(--text-muted)', opacity: hover ? 1 : 0.5 }} />
          )}
        </span>
      </div>
    )
  }

  return (
    <div
      onClick={clickable ? s.onClick : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: clickable ? '4px 8px' : '4px 0',
        marginLeft: clickable ? -8 : 0,
        borderRadius: 6,
        cursor: clickable ? 'pointer' : 'default',
        background: clickable && hover ? `${s.color}10` : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <span style={{
        fontFamily: 'ui-monospace, monospace',
        fontSize: 20, fontWeight: 800, color: s.color,
        opacity: loading ? 0.4 : 1,
      }}>
        {loading ? '—' : s.val}
      </span>
      <span style={{
        fontSize: 10, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
      }}>
        {s.label}
      </span>
      {clickable && (
        <ExternalLink
          size={10}
          style={{
            color: hover ? s.color : 'var(--text-muted)',
            opacity: hover ? 1 : 0.45,
            transition: 'all 0.15s',
            marginLeft: 2,
          }}
        />
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   FILTROS (Punto 1)
// ════════════════════════════════════════════════════════════════════════════
function FilterBar({
  filters, setFilters,
  onRefresh, isPaused, onTogglePause,
  leadCount, totalCount,
  lenders,
  sourceIdentifiers = [],
}) {
  const isMobile = useIsMobile()

  // El dropdown de lender se arma dinámicamente desde /waterfall/lenders.
  const lenderOptions = useMemo(() => {
    if (!lenders) return []
    // Mostramos todos los lenders (activos e inactivos) para poder filtrar
    // también leads históricos de lenders que ya se apagaron.
    return lenders.map(l => ({
      identifier: l.identifier,
      name: l.name || l.identifier,
      in_rotation: l.in_rotation,
    }))
  }, [lenders])

  // Texto guía al usuario sobre cómo combinan los filtros
  const hint = useMemo(() => {
    if (!filters.lender) return null
    if (!filters.state) {
      return 'Showing leads where this lender participated (rotation or auction)'
    }
    if (ROTATION_STATES.includes(filters.state)) {
      return 'Filtering by assigned turn in rotation'
    }
    if (AUCTION_STATES.includes(filters.state)) {
      return 'Filtering by final auction buyer'
    }
    return null
  }, [filters.state, filters.lender])

  // Estilo base de los selects, ancho completo en móvil.
  const selectStyle = {
    padding: '6px 10px', fontSize: 12, borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--bg-surface)',
    color: 'var(--text-primary)', cursor: 'pointer',
    width: isMobile ? '100%' : undefined,
    flex: isMobile ? '1 1 100%' : undefined,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 16px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)',
          flex: isMobile ? '1 1 100%' : undefined,
        }}>
          <Filter size={13} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>FILTERS</span>
        </div>

        <select
          value={filters.state || ''}
          onChange={(e) => setFilters({ ...filters, state: e.target.value || undefined })}
          style={selectStyle}
        >
          <option value="">All statuses</option>
          <option value="exclusive">In rotation</option>
          <option value="auction">In auction</option>
          <option value="won_exclusive">Rotation winner</option>
          <option value="won_auction">Auction winner</option>
          <option value="rejected_all">Rejected</option>
          <option value="error">Error</option>
        </select>

        <select
          value={filters.lender || ''}
          onChange={(e) => setFilters({ ...filters, lender: e.target.value || undefined })}
          style={selectStyle}
        >
          <option value="">All lenders</option>
          {lenderOptions.map(l => (
            <option key={l.identifier} value={l.identifier}>
              {l.name}
            </option>
          ))}
        </select>

        {/* Filtro por partner (source.identifier) — solo partners que
            tienen leads en waterfallLeadFlow. Vinovaleads y otros S2S
            puros no aparecen acá porque no entran al waterfall. */}
        <select
          value={filters.source_identifier || ''}
          onChange={(e) => setFilters({
            ...filters,
            source_identifier: e.target.value || undefined,
          })}
          style={selectStyle}
        >
          <option value="">All partners</option>
            {sourceIdentifiers.map(s => (
              <option key={s.identifier} value={s.identifier}>
                {s.identifier} ({s.count.toLocaleString()})
              </option>
            ))}
          </select>

        <label style={{
          display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
          cursor: 'pointer', color: 'var(--text-secondary)',
        }}>
          <input
            type="checkbox"
            checked={filters.is_test === true}
            onChange={(e) => setFilters({
              ...filters,
              is_test: e.target.checked ? true : undefined,
            })}
            style={{ cursor: 'pointer' }}
          />
          Test leads only
        </label>

        {!isMobile && <div style={{ flex: 1 }} />}

        <span style={{
          fontSize: 11, color: 'var(--text-muted)',
          marginRight: isMobile ? 'auto' : 0,
        }}>
          {totalCount != null
            ? `${leadCount} of ${totalCount} leads`
            : `${leadCount} leads visibles`}
        </span>

        <button
          onClick={onTogglePause}
          className="btn btn-ghost btn-sm"
          style={{
            background: isPaused ? 'var(--green-bg)' : 'var(--bg-raised)',
            color: isPaused ? 'var(--green)' : 'var(--text-secondary)',
          }}
        >
          {isPaused ? <Play size={12} /> : <Pause size={12} />}
          {isPaused ? 'Resume' : 'Pause'}
        </button>

        <button onClick={onRefresh} className="btn btn-ghost btn-sm">
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>
      {hint && (
        <div style={{
          fontSize: 10, color: 'var(--text-muted)',
          padding: '0 18px', fontStyle: 'italic',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Sparkles size={10} />
          {hint}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   PAGINACIÓN
// ════════════════════════════════════════════════════════════════════════════
function Pagination({ page, totalPages, onPageChange, total, limit }) {
  // Generar lista de páginas a mostrar: actual ± 2, primera, última, con elipsis
  const pages = useMemo(() => {
    const out = []
    const add = (p) => { if (!out.includes(p)) out.push(p) }
    add(1)
    for (let i = page - 2; i <= page + 2; i++) {
      if (i >= 1 && i <= totalPages) add(i)
    }
    add(totalPages)
    out.sort((a, b) => a - b)

    // Insertar gaps (null) donde haya saltos
    const withGaps = []
    out.forEach((p, idx) => {
      if (idx > 0 && p - out[idx - 1] > 1) withGaps.push(null)
      withGaps.push(p)
    })
    return withGaps
  }, [page, totalPages])

  if (totalPages <= 1) return null

  const start = (page - 1) * limit + 1
  const end = Math.min(page * limit, total)

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 10,
      padding: '12px 16px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        Showing <strong style={{ color: 'var(--text-primary)' }}>{start}–{end}</strong> of{' '}
        <strong style={{ color: 'var(--text-primary)' }}>{total}</strong> leads
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="btn btn-ghost btn-sm"
          aria-label="Previous page"
          style={{ padding: '4px 8px', opacity: page === 1 ? 0.4 : 1 }}
        >
          <ChevronLeft size={14} />
        </button>

        {pages.map((p, idx) => (
          p === null ? (
            <span key={`gap-${idx}`} style={{
              padding: '4px 6px', fontSize: 11, color: 'var(--text-muted)',
            }}>…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className="btn btn-ghost btn-sm"
              aria-label={`Page ${p}`}
              aria-current={p === page ? 'page' : undefined}
              style={{
                minWidth: 30,
                fontFamily: 'ui-monospace, monospace',
                fontWeight: p === page ? 800 : 500,
                background: p === page ? 'var(--accent-soft)' : 'transparent',
                color: p === page ? 'var(--accent)' : 'var(--text-secondary)',
                border: p === page ? '1px solid var(--accent)' : '1px solid transparent',
              }}
            >
              {p}
            </button>
          )
        ))}

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="btn btn-ghost btn-sm"
          aria-label="Next page"
          style={{ padding: '4px 8px', opacity: page === totalPages ? 0.4 : 1 }}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   COMPONENTES PEQUEÑOS
// ════════════════════════════════════════════════════════════════════════════
function LenderBadge({ identifier, size = 28, name }) {
  const style = getLenderStyle(identifier)
  const fontSize = size > 40 ? 22 : size > 30 ? 16 : 12
  return (
    <div title={name || identifier} style={{
      width: size, height: size, borderRadius: size > 40 ? 10 : 6,
      background: style.color, color: '#FFF',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize,
      fontFamily: 'ui-monospace, monospace',
      flexShrink: 0,
    }}>
      {style.letter}
    </div>
  )
}

function HttpBadge({ code }) {
  if (code == null) {
    return (
      <span style={{
        fontFamily: 'ui-monospace, monospace', fontSize: 10,
        color: 'var(--text-muted)',
      }}>
        HTTP —
      </span>
    )
  }
  return (
    <span style={{
      fontFamily: 'ui-monospace, monospace', fontSize: 10, fontWeight: 700,
      color: httpColor(code),
      background: 'var(--bg-raised)',
      padding: '2px 6px', borderRadius: 4,
      letterSpacing: 0.3,
    }}>
      HTTP {code}
    </span>
  )
}

function StateBadge({ state }) {
  const meta = getStateMeta(state)
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
      padding: '4px 9px', borderRadius: 4,
      background: meta.bg, color: meta.color,
      border: `1px solid ${meta.color}30`,
      fontFamily: 'ui-monospace, monospace',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {meta.label}
    </span>
  )
}

function CountdownRing({ expiresAt, durationSec, color = 'var(--amber)', size = 44 }) {
  const [remaining, setRemaining] = useState(0)

  useEffect(() => {
    if (!expiresAt) return
    const tick = () => {
      const exp = new Date(expiresAt).getTime()
      const now = Date.now()
      setRemaining(Math.max(0, Math.round((exp - now) / 1000)))
    }
    tick()
    const id = setInterval(tick, 500)
    return () => clearInterval(id)
  }, [expiresAt])

  const total = durationSec || 60
  const pct = Math.max(0, remaining / total)
  const stroke = 3
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const dash = c * pct

  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="var(--bg-raised)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${dash} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray 0.5s linear' }} />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        style={{
          fill: 'var(--text-primary)',
          fontSize: size > 40 ? 13 : 10, fontWeight: 700,
          fontFamily: 'ui-monospace, monospace',
        }}>
        {remaining}s
      </text>
    </svg>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   TARJETA DE LEAD (waterfall) — versión móvil de LeadRow
// ════════════════════════════════════════════════════════════════════════════
function LeadCardWaterfall({ lead, selected, onClick }) {
  const preferred = lead.preferred_lender || {}
  const final = lead.final || {}
  const { httpCode, timeDisplay } = getLeadRowDerived(lead)

  const labelStyle = {
    fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5,
    color: 'var(--text-muted)', fontWeight: 600, minWidth: 74, flexShrink: 0,
  }

  return (
    <div
      onClick={onClick}
      className="card"
      style={{
        padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
        cursor: 'pointer',
        borderLeft: selected ? '3px solid var(--accent)' : '3px solid transparent',
        background: selected ? 'var(--accent-soft)' : undefined,
      }}
    >
      {/* Lead ID + estado */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <span style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600,
          color: 'var(--text-primary)', wordBreak: 'break-all', minWidth: 0,
        }}>
          {lead.kickoff_lead_id}
        </span>
        <StateBadge state={lead.state} />
      </div>

      {/* Origen */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
        <span style={{
          width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
          background: lead.source?.kind === 'organic' ? 'var(--green)' : 'var(--blue)',
        }} />
        {lead.source?.identifier || '—'}
      </div>

      {/* Lender en rotación */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={labelStyle}>Rotation</span>
        {preferred.identifier ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
            <LenderBadge identifier={preferred.identifier} size={22} name={preferred.name} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>{preferred.name || preferred.identifier}</span>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>
              · turn #{lead.turn_number}
            </span>
          </div>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
        )}
      </div>

      {/* Comprador final */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={labelStyle}>Buyer</span>
        {final.winner_lender_identifier ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <LenderBadge identifier={final.winner_lender_identifier} size={20} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>
              {final.winner_lender_name || final.winner_lender_identifier}
            </span>
          </div>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
        )}
      </div>

      {/* Tiempo + HTTP */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: 8, borderTop: '1px solid var(--border)',
      }}>
        <span style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 11,
          color: lead.state === 'exclusive' ? 'var(--amber)'
               : lead.state === 'auction' ? 'var(--purple)'
               : 'var(--text-muted)',
        }}>
          {timeDisplay}
        </span>
        <HttpBadge code={httpCode} />
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   TABLA DE LEADS (waterfall)
// ════════════════════════════════════════════════════════════════════════════
function LeadsTable({ leads, selectedId, onSelect }) {
  const isMobile = useIsMobile()

  if (leads.length === 0) {
    return (
      <div className="card" style={{
        padding: 40, textAlign: 'center',
        color: 'var(--text-muted)',
      }}>
        <Activity size={28} style={{ opacity: 0.4 }} />
        <p style={{ marginTop: 12, fontSize: 13 }}>
          No leads match the current filters.
        </p>
      </div>
    )
  }

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {leads.map((lead) => (
          <LeadCardWaterfall
            key={lead.kickoff_lead_id}
            lead={lead}
            selected={lead.kickoff_lead_id === selectedId}
            onClick={() => onSelect(lead.kickoff_lead_id)}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '75px 135px minmax(130px, 1fr) 100px minmax(140px, 1fr) minmax(140px, 1.2fr) 150px',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
        fontSize: 10, color: 'var(--text-muted)',
        letterSpacing: 1.1, fontWeight: 700,
        background: 'var(--bg-raised)',
        position: 'sticky', top: 0, zIndex: 1,
        fontFamily: 'ui-monospace, monospace',
        alignItems: 'center'
      }}>
        <span>LEAD ID</span>
        <span>SOURCE</span>
        <span>ROTATION LENDER</span>
        <span>AGE</span>
        <span>FINAL BUYER</span>
        <span>BUYER BIDS & ROUTING</span>
        <span style={{ textAlign: 'right' }}>STATUS / RESULT</span>
      </div>

      <div style={{
        minHeight: 420,
        maxHeight: 'calc(100vh - 400px)',
        overflowY: 'auto',
      }}>
        {leads.map((lead) => (
          <LeadRow
            key={lead.kickoff_lead_id || lead._id}
            lead={lead}
            selected={lead.kickoff_lead_id === selectedId}
            onClick={() => onSelect(lead.kickoff_lead_id)}
          />
        ))}
      </div>
    </div>
  )
}

function LeadRow({ lead, selected, onClick }) {
  const preferred = lead.preferred_lender || {}
  const final = lead.final || {}
  const auction = lead.auction || {}
  const events = lead.events || []
  const { httpCode, timeDisplay } = getLeadRowDerived(lead)

  const formattedWinnerName = final.winner_lender_name
    || final.winner_name
    || (final.winner_lender_identifier
      ? final.winner_lender_identifier
          .replace(/^cleanenergy_llc$/i, 'CleanEnergy LLC')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase())
      : null)

  // Extract all buyers that were attempted / bid on this lead
  const auctionParticipants = auction.participants || []
  const hasAuctionBids = auctionParticipants.length > 0

  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: '75px 135px minmax(130px, 1fr) 100px minmax(140px, 1fr) minmax(140px, 1.2fr) 150px',
        gap: 12,
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        background: selected ? 'var(--accent-soft)' : 'transparent',
        borderLeft: selected ? `3px solid var(--accent)` : '3px solid transparent',
        transition: 'background 0.1s',
        alignItems: 'center',
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--bg-raised)' }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent' }}
    >
      <div style={{
        fontFamily: 'ui-monospace, monospace', fontSize: 12,
        color: 'var(--text-primary)', fontWeight: 700,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }} title={lead.kickoff_lead_id}>
        {String(lead.kickoff_lead_id || '').slice(0, 12)}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-secondary)', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            background: lead.source?.kind === 'organic' ? 'var(--green)' : 'var(--blue)',
          }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {lead.source?.identifier || '—'}
          </span>
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        {preferred.identifier ? (
          <div>
            <div style={{
              fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
            }} title={preferred.name || preferred.identifier}>
              {preferred.name || preferred.identifier}
            </div>
            {lead.turn_number && (
              <div style={{
                fontSize: 10, color: 'var(--text-muted)',
                fontFamily: 'ui-monospace, monospace',
              }}>
                turn #{lead.turn_number}
              </div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
        )}
      </div>

      <div style={{
        fontFamily: 'ui-monospace, monospace', fontSize: 11,
        color: lead.state === 'exclusive' ? 'var(--amber)'
             : lead.state === 'auction' ? 'var(--purple)'
             : 'var(--text-muted)',
        whiteSpace: 'nowrap'
      }}>
        {timeDisplay}
      </div>

      <div style={{ fontSize: 11, minWidth: 0 }}>
        {formattedWinnerName ? (
          <span style={{
            color: 'var(--text-primary)', fontWeight: 600,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            display: 'block'
          }} title={formattedWinnerName}>
            {formattedWinnerName}
          </span>
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>—</span>
        )}
      </div>

      {/* BUYER BIDS & ROUTING SUMMARY COLUMN */}
      <div style={{ minWidth: 0 }}>
        {hasAuctionBids ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            {auctionParticipants.slice(0, 2).map((p, idx) => {
              const isWinner = final.winner_lender_identifier === p.lender_identifier
              const isAccepted = p.result === 'accepted_bid'
              return (
                <span key={idx} style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                  background: isWinner ? 'var(--green-bg)' : isAccepted ? 'var(--blue-bg)' : 'var(--red-bg)',
                  color: isWinner ? 'var(--green)' : isAccepted ? 'var(--blue)' : 'var(--red)',
                  fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap'
                }}>
                  {p.lender_name || p.lender_identifier}: {p.bid_usd ? `$${p.bid_usd.toFixed(2)}` : 'BID'}
                </span>
              )
            })}
            {auctionParticipants.length > 2 && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>
                +{auctionParticipants.length - 2} more
              </span>
            )}
          </div>
        ) : preferred.identifier ? (
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
            background: 'var(--amber-bg)', color: 'var(--amber)',
            fontFamily: 'ui-monospace, monospace'
          }}>
            1:1 Direct → {preferred.name || preferred.identifier}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No buyer pings</span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
        <StateBadge state={lead.state} />
        {httpCode != null && <HttpBadge code={httpCode} />}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   PRE-ROTACIÓN · ENGINE (Punto 3)
//   Deduce por qué este lead NO fue a Engine y entró al waterfall.
// ════════════════════════════════════════════════════════════════════════════
function EngineReasoning({ contact }) {
  if (contact?.engine_reason) {
    const formattedReason = contact.engine_reason
      .replace(/on_ccpa_optout_suppression_list/g, 'CCPA Opt-Out Suppression List')
      .replace(/_/g, ' ')

    const isBlocked = contact.engine_reason.toLowerCase().includes('blocked') || contact.engine_reason.toLowerCase().includes('rejected')

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', maxWidth: '100%' }}>
        <span style={{
          fontSize: 10, fontWeight: 700,
          padding: '2px 8px', borderRadius: 4,
          background: isBlocked ? 'var(--red-bg)' : 'var(--bg-raised)',
          color: isBlocked ? 'var(--red)' : 'var(--text-secondary)',
          letterSpacing: 0.3,
          whiteSpace: 'nowrap'
        }}>
          {isBlocked ? 'Intake Compliance Gate' : 'Pre-rotation Gate'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', wordBreak: 'break-word', maxWidth: 220 }}>
          {formattedReason}
        </span>
      </div>
    )
  }

  const hasFields = contact.has_email !== undefined
    && contact.has_ssn !== undefined
    && contact.annual_income !== undefined

  if (!hasFields) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 12, color: 'var(--text-muted)',
      }}>
        <AlertCircle size={13} />
        <span>Cannot be determined (lead predates the Engine patch)</span>
      </div>
    )
  }

  const hasEmail = !!contact.has_email
  const hasSsn   = !!contact.has_ssn
  const income   = Number(contact.annual_income || 0)

  const qualifies = hasEmail && hasSsn && income >= ENGINE_MIN_INCOME

  if (!qualifies) {
    const reasons = []
    if (!hasEmail) reasons.push('falta email')
    if (!hasSsn)   reasons.push('falta SSN')
    if (income < ENGINE_MIN_INCOME) {
      reasons.push(`ingreso ${fmtMoney(income)} < ${fmtMoney(ENGINE_MIN_INCOME)}`)
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 11, fontWeight: 700,
          padding: '3px 8px', borderRadius: 4,
          background: 'var(--bg-raised)',
          color: 'var(--text-secondary)',
          letterSpacing: 0.3,
        }}>
          Did not qualify for Engine
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {reasons.join(' · ')}
        </span>
      </div>
    )
  }

  // Calificaba pero perdió el sorteo 30/70 (porque está en el waterfall).
  // La probabilidad de Engine es 30%. Probabilidad de caer al Direct Stack = 70%.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{
        fontSize: 11, fontWeight: 700,
        padding: '3px 8px', borderRadius: 4,
        background: 'var(--amber-bg)',
        color: 'var(--amber)',
        letterSpacing: 0.3,
      }}>
        Qualified · lost the draw
      </span>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        70% goes to the waterfall · 30% goes to Engine
      </span>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   PANEL DE DETALLE DEL LEAD — 4-5 etapas
// ════════════════════════════════════════════════════════════════════════════
function LeadDetailPanel({ lead }) {
  if (!lead) {
    return (
      <div className="card" style={{
        padding: 40, textAlign: 'center',
        color: 'var(--text-muted)',
        height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div>
          <Layers size={28} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: 12, fontSize: 13 }}>
            Select a lead from the table to see its full journey
          </p>
        </div>
      </div>
    )
  }

  const preferred = lead.preferred_lender || {}
  const exclusive = lead.exclusive || {}
  const auction = lead.auction || {}
  const final = lead.final || {}
  const events = lead.events || []
  const contact = lead.contact || {}

  const hadAuction = lead.state === 'auction'
    || lead.state === 'won_auction'
    || lead.state === 'rejected_all'

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{
                fontFamily: 'ui-monospace, monospace', fontSize: 15, fontWeight: 700,
                color: 'var(--text-primary)', wordBreak: 'break-all',
              }} title={lead.kickoff_lead_id}>
                {lead.kickoff_lead_id}
              </span>
            </div>
            <div style={{
              display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-muted)',
              flexWrap: 'wrap',
            }}>
              <span>Turn #{lead.turn_number ?? '—'}</span>
              <span>·</span>
              <span>{fmtDateTime(lead.createdAt)}</span>
              {lead.is_test && (
                <>
                  <span>·</span>
                  <span className="badge badge-purple" style={{ fontSize: 9, padding: '1px 6px' }}>TEST</span>
                </>
              )}
            </div>
          </div>
          <StateBadge state={lead.state} />
        </div>
      </div>

      {/* Body scroll */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* ETAPA 01: Origen */}
          <Stage
            number="01"
            title="Lead source"
            state="done"
            color="var(--blue)"
            right={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Globe2 size={14} style={{ color: 'var(--text-muted)' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {lead.source?.identifier || '—'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    {lead.source?.kind || '—'}
                    {lead.source?.partner_aff_id && ` · ${lead.source.partner_aff_id}`}
                  </div>
                </div>
              </div>
            }
          />

          {/* ETAPA 02: Pre-rotación · Engine (Punto 3) */}
          <Stage
            number="02"
            title="Pre-rotation · Engine"
            state="done"
            color="#7C3AED"
            right={<EngineReasoning contact={contact} />}
          />

          {/* ETAPA 03: Rotación exclusiva */}
          <Stage
            number="03"
            title="Exclusive rotation"
            state={
              lead.state === 'exclusive' ? 'active'
                : lead.state === 'won_exclusive' ? 'success'
                : (lead.state === 'intake' ? 'pending' : 'done')
            }
            color={getLenderStyle(preferred.identifier).color}
            right={
              preferred.identifier ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <LenderBadge identifier={preferred.identifier} size={32} name={preferred.name} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {preferred.name || preferred.identifier}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: 10, color: 'var(--text-muted)', marginTop: 2,
                      flexWrap: 'wrap',
                    }}>
                      <span>
                        {lead.state === 'exclusive'
                          ? `Rotation open ${exclusive.duration_sec}s`
                          : exclusive.result === 'accepted_bid' ? 'accepted'
                          : exclusive.result === 'rejected' ? 'rejected'
                          : exclusive.result === 'timeout' ? 'no response'
                          : exclusive.result === 'error' ? 'error'
                          : '—'}
                      </span>
                      {exclusive.elapsed_ms != null && (
                        <span style={{ fontFamily: 'ui-monospace, monospace' }}>
                          · {fmtMs(exclusive.elapsed_ms)}
                        </span>
                      )}
                      {exclusive.http_status_code != null && (
                        <HttpBadge code={exclusive.http_status_code} />
                      )}
                    </div>
                  </div>
                  {lead.state === 'exclusive' && exclusive.expires_at && (
                    <CountdownRing
                      expiresAt={exclusive.expires_at}
                      durationSec={exclusive.duration_sec}
                      color={getLenderStyle(preferred.identifier).color}
                      size={44}
                    />
                  )}
                </div>
              ) : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
            }
          />

          {/* ETAPA 04: Subasta */}
          {hadAuction && (
            <Stage
              number="04"
              title="Parallel auction"
              state={lead.state === 'auction' ? 'active' : 'done'}
              color="var(--purple)"
              right={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {(auction.participants || []).map((p) => {
                      const style = getLenderStyle(p.lender_identifier)
                      const isWinner = final.winner_lender_identifier === p.lender_identifier
                      return (
                        <div key={p.lender_identifier} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '4px 8px', borderRadius: 6,
                          background: isWinner ? `${style.color}15`
                            : p.result === 'rejected' ? 'var(--red-bg)'
                            : p.result === 'error'    ? 'var(--red-bg)'
                            : p.result === 'timeout'  ? 'var(--amber-bg)'
                            : 'var(--bg-raised)',
                          border: `1px solid ${
                            isWinner ? style.color
                              : p.result === 'rejected' ? 'var(--red)'
                              : p.result === 'error'    ? 'var(--red)'
                              : p.result === 'timeout'  ? 'var(--amber)'
                              : 'var(--border)'
                          }30`,
                        }}>
                          <LenderBadge identifier={p.lender_identifier} size={16} />
                          <span style={{ fontSize: 10, fontWeight: 600 }}>
                            {p.lender_name || p.lender_identifier}
                          </span>
                          <HttpBadge code={p.http_status_code} />
                          <span style={{
                            fontSize: 10, fontFamily: 'ui-monospace, monospace',
                            color: isWinner ? style.color
                              : p.result === 'rejected' ? 'var(--red)'
                              : p.result === 'error' ? 'var(--red)'
                              : p.result === 'timeout' ? 'var(--amber)'
                              : 'var(--text-muted)',
                            fontWeight: 600,
                          }}>
                            {p.result === 'accepted_bid' && isWinner ? `WIN ${fmtMoney(p.bid_usd)}`
                              : p.result === 'accepted_bid' ? `bid ${fmtMoney(p.bid_usd)}`
                              : p.result === 'rejected' ? 'rejected'
                              : p.result === 'error' ? 'error'
                              : p.result === 'timeout' ? 'timeout'
                              : p.result === 'pending' ? '...' : '—'}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              }
            />
          )}

          {/* ETAPA FINAL: Resultado */}
          <Stage
            number={hadAuction ? '05' : '04'}
            title="Final result"
            state={
              lead.state === 'won_exclusive' || lead.state === 'won_auction' ? 'success'
                : lead.state === 'rejected_all' ? 'fail'
                : lead.state === 'error' ? 'fail'
                : 'pending'
            }
            color={
              lead.state === 'won_exclusive' ? 'var(--green)'
                : lead.state === 'won_auction' ? 'var(--blue)'
                : lead.state === 'rejected_all' || lead.state === 'error' ? 'var(--red)'
                : 'var(--text-muted)'
            }
            right={
              final.winner_lender_identifier ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <LenderBadge identifier={final.winner_lender_identifier} size={32} />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>
                        {final.winner_lender_name || final.winner_lender_identifier}
                      </span>
                      <HttpBadge code={final.winner_http_status_code} />
                    </div>
                    <div style={{
                      fontSize: 10, color: 'var(--text-muted)', marginTop: 2,
                      display: 'flex', gap: 6, alignItems: 'center',
                    }}>
                      <span style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--green)' }}>
                        {fmtMoney(final.winning_bid_usd)}
                      </span>
                      <span>·</span>
                      <span>
                        {final.via === 'exclusive_window' ? 'via exclusive rotation' : 'via auction'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : lead.state === 'rejected_all' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <XCircle size={14} style={{ color: 'var(--red)' }} />
                  <span style={{ fontSize: 12, color: 'var(--red)' }}>
                    No buyers · lead lost
                  </span>
                  {final.via === 'hyalite_fallback' && (
                    <span className="badge badge-purple" style={{ fontSize: 9, padding: '1px 6px' }}>
                      → HYALITE
                    </span>
                  )}
                </div>
              ) : lead.state === 'error' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle size={14} style={{ color: 'var(--red)' }} />
                  <span style={{ fontSize: 12, color: 'var(--red)' }}>
                    Technical error
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>pending...</span>
              )
            }
          />
        </div>

        {/* EVENT LOG */}
        <div style={{
          marginTop: 16, padding: 14,
          background: 'var(--bg-raised)',
          borderRadius: 10,
          border: '1px solid var(--border)',
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
            color: 'var(--text-muted)', marginBottom: 10,
            display: 'flex', alignItems: 'center', gap: 6
          }}>
            <Clock size={12} /> EVENT LOG ({events.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {events.length === 0 ? (
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No events recorded.</span>
            ) : (
              events.map((e, i) => {
                const timeVal = e.timestamp || e.created_at || e.ts
                const textVal = e.message || e.event_type_label || e.event || e.event_type || e.text || 'Event recorded'
                return (
                  <div key={e._id || i} style={{
                    display: 'flex', gap: 10, alignItems: 'baseline',
                    fontFamily: 'ui-monospace, monospace', fontSize: 11,
                  }}>
                    <span style={{ color: 'var(--text-muted)', minWidth: 64, flexShrink: 0 }}>
                      {fmtTime(timeVal)}
                    </span>
                    <span style={{ color: 'var(--text-muted)' }}>·</span>
                    <span style={{ color: 'var(--text-primary)', flex: 1, wordBreak: 'break-word' }}>
                      {textVal}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Stage({ number, title, state, color, right }) {
  const isMobile = useIsMobile()
  const stateConfig = {
    pending: { dot: 'var(--border-strong)', icon: null, opacity: 0.55 },
    active:  { dot: color, icon: <Activity size={11} />, opacity: 1 },
    done:    { dot: color, icon: null, opacity: 1 },
    success: { dot: 'var(--green)', icon: <CheckCircle2 size={11} />, opacity: 1 },
    fail:    { dot: 'var(--red)', icon: <XCircle size={11} />, opacity: 1 },
    skipped: { dot: 'var(--border-strong)', icon: null, opacity: 0.4 },
  }
  const s = stateConfig[state] || stateConfig.pending

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      padding: '14px 16px',
      background: state === 'active' ? `${color}08` : 'var(--bg-raised)',
      border: state === 'active' ? `1px solid ${color}50` : '1px solid var(--border)',
      borderRadius: 10,
      opacity: s.opacity,
      transition: 'all 0.2s ease',
      overflow: 'hidden'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{
            fontFamily: 'ui-monospace, monospace', fontSize: 11,
            fontWeight: 800, color: 'var(--text-muted)',
            background: 'var(--bg-surface)',
            padding: '2px 6px', borderRadius: 4,
            border: '1px solid var(--border)',
            letterSpacing: 0.5,
          }}>
            {number}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: s.dot,
              boxShadow: state === 'active' ? `0 0 0 3px ${s.dot}30` : 'none',
              animation: state === 'active' ? 'pulse-dot 1.6s ease-in-out infinite' : 'none',
              flexShrink: 0,
            }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
              {title}
            </span>
            {s.icon && (
              <span style={{ color: state === 'success' ? 'var(--green)' : state === 'fail' ? 'var(--red)' : color }}>
                {s.icon}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{
        width: '100%',
        paddingTop: 8,
        borderTop: '1px solid var(--border)',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        minWidth: 0,
        overflow: 'hidden'
      }}>
        {right}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   SHEET MÓVIL — contenedor deslizable para el detalle del lead
// ════════════════════════════════════════════════════════════════════════════
function MobileDetailSheet({ onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        zIndex: 1000,
        animation: 'fadeIn 0.15s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', height: '92vh',
          padding: '0 12px 12px',
          display: 'flex', flexDirection: 'column',
          animation: 'slideUp 0.2s ease-out',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 0 8px' }}>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
          >
            <X size={14} /> Close
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          {children}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   VISTA ENGINE (Punto 3) — leads que se fueron por Engine y NO entraron
//   al waterfall. Tabla aparte que lee de /waterfall/engine/leads.
// ════════════════════════════════════════════════════════════════════════════
// Helper: calcular el instante "00:00 hora Colombia (UTC-5)" de hoy
// y devolverlo como ISO UTC para mandar al backend.
//
// Ejemplo: si en Colombia son las 14:30 del 27 de mayo, devuelve
// "2026-05-27T05:00:00.000Z" (que es 00:00 del 27 en hora Colombia).
//
// Usamos UTC-5 fijo porque Colombia no tiene DST. Si en algún momento
// se hace multi-region, esto debería leer un offset configurable.
const COLOMBIA_UTC_OFFSET_HOURS = -5

function getTodayStartIsoUtc() {
  const now = new Date()
  // Lo que es "ahora" para Colombia: now en UTC + (-5h offset) = ms en Colombia
  const colombiaNowMs = now.getTime() + COLOMBIA_UTC_OFFSET_HOURS * 3600 * 1000
  const colombiaNow = new Date(colombiaNowMs)
  // Tomamos solo año/mes/día y armamos 00:00 en Colombia
  const y = colombiaNow.getUTCFullYear()
  const m = colombiaNow.getUTCMonth()
  const d = colombiaNow.getUTCDate()
  // 00:00 hora Colombia = 05:00 UTC del mismo día calendario
  const todayStartUtcMs = Date.UTC(y, m, d, -COLOMBIA_UTC_OFFSET_HOURS, 0, 0)
  return new Date(todayStartUtcMs).toISOString()
}

// Pastilla OK / NO URL para el redirect de Engine (reutilizada en tabla y tarjeta).
function EngineRedirectBadge({ lead }) {
  if (lead.has_redirect) {
    return (
      <a
        href={lead.partnerpage_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, fontWeight: 700,
          color: 'var(--green)',
          padding: '2px 6px', borderRadius: 4,
          background: 'var(--green-bg)',
          textDecoration: 'none',
        }}
      >
        OK <ExternalLink size={10} />
      </a>
    )
  }
  return (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: 'var(--red)',
      padding: '2px 6px', borderRadius: 4,
      background: 'var(--red-bg)',
    }}>
      NO URL
    </span>
  )
}

function EngineLeadCard({ lead }) {
  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <span style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600,
          color: 'var(--text-primary)', wordBreak: 'break-all', minWidth: 0,
        }}>
          {lead.kickoff_lead_id || '—'}
        </span>
        <EngineRedirectBadge lead={lead} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <HttpBadge code={lead.http_status} />
        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'var(--text-muted)' }}>
          {lead.elapsed_sec != null ? `${lead.elapsed_sec.toFixed(2)}s` : '—'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {fmtRelative(lead.createdAt)}
        </span>
      </div>

      <div>
        <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', fontWeight: 600 }}>
          Lead UUID (Engine)
        </div>
        <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
          {lead.lead_uuid || '—'}
        </div>
      </div>
    </div>
  )
}

function EngineView() {
  const isMobile = useIsMobile()
  const [data, setData] = useState({ leads: [], total: 0, total_pages: 1 })
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('all')
  // Default: filtro "Today only" ACTIVADO (limpia ruido de leads viejos
  // con timestamps mal serializados antes del fix de UTC).
  const [onlyToday, setOnlyToday] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      const params = { page, limit: PAGE_SIZE }
      if (statusFilter !== 'all') params.status = statusFilter
      if (onlyToday) params.since = getTodayStartIsoUtc()
      const { data } = await api.get('/waterfall/engine/leads', { params })
      setData(data)
      setError(null)
    } catch (err) {
      console.error('[engine] fetch failed:', err)
      setError(err.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, onlyToday])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Reset page cuando cambia status o el filtro de hoy
  useEffect(() => {
    setPage(1)
  }, [statusFilter, onlyToday])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Aviso explicativo */}
      <div style={{
        padding: '12px 16px', borderRadius: 'var(--radius)',
        background: 'rgba(124,58,237,0.08)',
        border: '1px solid rgba(124,58,237,0.25)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Sparkles size={16} style={{ color: '#7C3AED', flexShrink: 0 }} />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            The draw to Engine happens <strong>BEFORE</strong> the waterfall. Leads that
            win the 30/70 draw are redirected to Engine and{' '}
            <strong>never enter the rotator</strong>. If Engine fails <strong>IT FALLS BACK TO THE ROTATOR</strong>.
          </div>
      </div>

      {/* Filtro + paginación */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 16px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)',
          flex: isMobile ? '1 1 100%' : undefined,
        }}>
          <Filter size={13} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>STATUS</span>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '6px 10px', fontSize: 12, borderRadius: 6,
            border: '1px solid var(--border)', background: 'var(--bg-surface)',
            color: 'var(--text-primary)', cursor: 'pointer',
            width: isMobile ? '100%' : undefined,
            flex: isMobile ? '1 1 100%' : undefined,
          }}
        >
          <option value="all">All</option>
          <option value="success">Successful (HTTP 200 + redirect)</option>
          <option value="error">Errors</option>
        </select>

        <label style={{
          display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
          cursor: 'pointer', color: 'var(--text-secondary)',
          padding: '6px 10px', borderRadius: 6,
          background: onlyToday ? 'var(--accent-soft)' : 'transparent',
          border: `1px solid ${onlyToday ? 'var(--accent)' : 'var(--border)'}`,
          transition: 'all 0.15s',
        }}>
          <input
            type="checkbox"
            checked={onlyToday}
            onChange={(e) => setOnlyToday(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ fontWeight: onlyToday ? 700 : 500 }}>
            Today only
          </span>
        </label>

        {!isMobile && <div style={{ flex: 1 }} />}

        <span style={{
          fontSize: 11, color: 'var(--text-muted)',
          marginRight: isMobile ? 'auto' : 0,
        }}>
          {data.total} leads{onlyToday ? ' today' : ' total'} in Engine
        </span>
        <button onClick={fetchData} className="btn btn-ghost btn-sm">
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 'var(--radius)',
          background: 'var(--red-bg)', border: '1px solid var(--red)',
          fontSize: 12, color: 'var(--red)',
        }}>
          Error: {String(error)}
        </div>
      )}

      {/* Tabla / tarjetas */}
      {data.leads.length === 0 ? (
        <div className="card" style={{
          padding: 40, textAlign: 'center',
          color: 'var(--text-muted)',
        }}>
          <Activity size={28} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: 12, fontSize: 13 }}>
            {loading ? 'Loading…' : 'No leads in Engine with the current filters.'}
          </p>
        </div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data.leads.map((l) => (
            <EngineLeadCard key={l._id} lead={l} />
          ))}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '160px 100px 120px 140px 1fr 90px',
            gap: 12,
            padding: '10px 16px',
            borderBottom: '1px solid var(--border)',
            fontSize: 9, color: 'var(--text-muted)',
            letterSpacing: 1.2, fontWeight: 700,
            background: 'var(--bg-raised)',
            fontFamily: 'ui-monospace, monospace',
          }}>
            <span>KICKOFF LEAD ID</span>
            <span>HTTP</span>
            <span>TIME</span>
            <span>CREATED</span>
            <span>LEAD UUID (ENGINE)</span>
            <span>REDIRECT</span>
          </div>
          {data.leads.map((l) => (
            <div key={l._id} style={{
              display: 'grid',
              gridTemplateColumns: '160px 100px 120px 140px 1fr 90px',
              gap: 12,
              padding: '10px 16px',
              borderBottom: '1px solid var(--border)',
              fontSize: 11, alignItems: 'center',
            }}>
              <span style={{
                fontFamily: 'ui-monospace, monospace',
                fontWeight: 600, color: 'var(--text-primary)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }} title={l.kickoff_lead_id}>
                {l.kickoff_lead_id || '—'}
              </span>
              <HttpBadge code={l.http_status} />
              <span style={{
                fontFamily: 'ui-monospace, monospace',
                color: 'var(--text-muted)',
              }}>
                {l.elapsed_sec != null ? `${l.elapsed_sec.toFixed(2)}s` : '—'}
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                {fmtRelative(l.createdAt)}
              </span>
              <span style={{
                fontFamily: 'ui-monospace, monospace', fontSize: 10,
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }} title={l.lead_uuid || ''}>
                {l.lead_uuid || '—'}
              </span>
              <span>
                <EngineRedirectBadge lead={l} />
              </span>
            </div>
          ))}
        </div>
      )}

      <Pagination
        page={data.page || 1}
        totalPages={data.total_pages || 1}
        total={data.total || 0}
        limit={data.limit || PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   MODAL · LEADS RECHAZADOS ANTES DEL WATERFALL
//   Se abre al click en el contador "Rechazados" del summary.
//   Lee de /waterfall/rejected-leads (partnerLeads con status="rejected").
//   Permite filtrar por motivo y paginación numerada.
// ════════════════════════════════════════════════════════════════════════════

// Paleta para badges de razones de rechazo. Si aparece una razón nueva
// que no esté acá, cae al gris por defecto.
const REJECTION_REASON_STYLES = {
  DUPLICATE_LEAD:        { color: 'var(--amber)',  bg: 'var(--amber-bg)',  label: 'Duplicate' },
  TCPA:                  { color: 'var(--red)',    bg: 'var(--red-bg)',    label: 'TCPA' },
  TCPA_MISSING:          { color: 'var(--red)',    bg: 'var(--red-bg)',    label: 'TCPA missing' },
  INVALID_DATA:          { color: 'var(--red)',    bg: 'var(--red-bg)',    label: 'Invalid data' },
  MISSING_FIELDS:        { color: 'var(--red)',    bg: 'var(--red-bg)',    label: 'Missing fields' },
  BLOCKED_STATE:         { color: 'var(--purple)', bg: 'var(--purple-bg)', label: 'Blocked state' },
  SMS_SUPPRESSED:        { color: 'var(--purple)', bg: 'var(--purple-bg)', label: 'SMS suppressed' },
  UNKNOWN:               { color: 'var(--text-muted)', bg: 'var(--bg-raised)', label: 'No reason' },
}

function getReasonStyle(reason) {
  return REJECTION_REASON_STYLES[reason] || {
    color: 'var(--text-muted)',
    bg: 'var(--bg-raised)',
    label: reason || 'No reason',
  }
}

function RejectedLeadsModal({ open, onClose, summary, dateRange }) {
  const isMobile = useIsMobile()
  const [data, setData] = useState({ leads: [], total: 0, total_pages: 1 })
  const [page, setPage] = useState(1)
  const [reasonFilter, setReasonFilter] = useState(null)
  const [loading, setLoading] = useState(false)

  // Cerrar con ESC
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setPage(1)
      setReasonFilter(null)
    }
  }, [open])

  // Fetch
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    const params = { page, limit: 100 }
    if (reasonFilter) params.reason = reasonFilter
    const since = getDateRangeSinceIso(dateRange)
    if (since) params.since = since
    api.get('/waterfall/rejected-leads', { params })
      .then(({ data }) => {
        if (!cancelled) setData(data)
      })
      .catch((err) => {
        console.error('[rejected] fetch failed:', err)
        if (!cancelled) toast.error('Error loading rejected leads')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, page, reasonFilter, dateRange])

  // Reset page al cambiar filtro
  useEffect(() => {
    setPage(1)
  }, [reasonFilter])

  if (!open) return null

  const reasonsBreakdown = summary?.rejected_by_reason || {}
  const reasonChips = Object.entries(reasonsBreakdown)
    .sort((a, b) => b[1] - a[1])  // más frecuentes primero

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? 12 : 24, zIndex: 1000,
        animation: 'fadeIn 0.15s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? '100%' : '90%', maxWidth: 1100,
          maxHeight: isMobile ? '90vh' : '85vh',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideUp 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? '14px 16px' : '18px 24px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-raised)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Ban size={18} style={{ color: 'var(--red)', flexShrink: 0 }} />
              <h2 style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, margin: 0 }}>
                Leads rejected before the waterfall
              </h2>
            </div>
            <p style={{
              margin: '4px 0 0 28px', fontSize: 11,
              color: 'var(--text-muted)',
            }}>
              {summary?.rejected_before_waterfall ?? '—'} rejected in total
              {' '}since May 21 · read from partnerLeads
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            style={{ padding: '6px 10px', flexShrink: 0 }}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Filtros por razón */}
        {reasonChips.length > 0 && (
          <div style={{
            padding: isMobile ? '12px 16px' : '12px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.2,
              color: 'var(--text-muted)',
            }}>
              FILTER BY REASON
            </span>
            <ReasonChip
              reason={null}
              count={summary?.rejected_before_waterfall ?? 0}
              active={reasonFilter === null}
              onClick={() => setReasonFilter(null)}
              labelOverride="All"
            />
            {reasonChips.map(([reason, count]) => (
              <ReasonChip
                key={reason}
                reason={reason}
                count={count}
                active={reasonFilter === reason}
                onClick={() => setReasonFilter(reason)}
              />
            ))}
          </div>
        )}

        {/* Tabla / tarjetas con scroll */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && data.leads.length === 0 ? (
            <div style={{
              padding: 60, textAlign: 'center', color: 'var(--text-muted)',
            }}>
              <RefreshCw size={24} style={{ animation: 'spin 1.4s linear infinite' }} />
              <p style={{ marginTop: 12, fontSize: 13 }}>Loading…</p>
            </div>
          ) : data.leads.length === 0 ? (
            <div style={{
              padding: 60, textAlign: 'center', color: 'var(--text-muted)',
            }}>
              <Ban size={28} style={{ opacity: 0.4 }} />
              <p style={{ marginTop: 12, fontSize: 13 }}>
                No rejected leads with this filter.
              </p>
            </div>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14 }}>
              {data.leads.map((l) => {
                const reasonStyle = getReasonStyle(l.rejection_reason)
                return (
                  <div key={l._id} className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{
                        fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600,
                        color: 'var(--text-primary)', wordBreak: 'break-all', minWidth: 0,
                      }}>
                        {l.kickoff_lead_id || '—'}
                      </span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                        padding: '3px 8px', borderRadius: 4,
                        background: reasonStyle.bg, color: reasonStyle.color,
                        border: `1px solid ${reasonStyle.color}30`,
                        fontFamily: 'ui-monospace, monospace', flexShrink: 0,
                      }}>
                        {reasonStyle.label}
                      </span>
                    </div>
                    {l.rejection_detail && (
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {l.rejection_detail}
                      </span>
                    )}
                    <span style={{
                      fontSize: 10, color: 'var(--text-muted)',
                      fontFamily: 'ui-monospace, monospace',
                    }} title={fmtDateTime(l.createdAt)}>
                      {fmtRelative(l.createdAt)}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '320px 180px 1fr 150px',
                gap: 12,
                padding: '10px 24px',
                borderBottom: '1px solid var(--border)',
                fontSize: 9, color: 'var(--text-muted)',
                letterSpacing: 1.2, fontWeight: 700,
                background: 'var(--bg-raised)',
                position: 'sticky', top: 0, zIndex: 1,
                fontFamily: 'ui-monospace, monospace',
              }}>
                <span>KICKOFF LEAD ID</span>
                <span>REASON</span>
                <span>DETAIL</span>
                <span>DATE</span>
              </div>
              {data.leads.map((l) => {
                const reasonStyle = getReasonStyle(l.rejection_reason)
                return (
                  <div key={l._id} style={{
                    display: 'grid',
                    gridTemplateColumns: '320px 180px 1fr 150px',
                    gap: 12,
                    padding: '12px 24px',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 11, alignItems: 'center',
                  }}>
                    <span style={{
                      fontFamily: 'ui-monospace, monospace',
                      fontWeight: 600, color: 'var(--text-primary)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={l.kickoff_lead_id}>
                      {l.kickoff_lead_id || '—'}
                    </span>
                    <span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                        padding: '3px 8px', borderRadius: 4,
                        background: reasonStyle.bg,
                        color: reasonStyle.color,
                        border: `1px solid ${reasonStyle.color}30`,
                        fontFamily: 'ui-monospace, monospace',
                      }}>
                        {reasonStyle.label}
                      </span>
                    </span>
                    <span style={{
                      color: 'var(--text-secondary)',
                      fontSize: 11,
                    }} title={l.rejection_detail}>
                      {l.rejection_detail || '—'}
                    </span>
                    <span style={{
                      color: 'var(--text-muted)',
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: 10,
                    }} title={fmtDateTime(l.createdAt)}>
                      {fmtRelative(l.createdAt)}
                    </span>
                  </div>
                )
              })}
            </>
          )}
        </div>

        {/* Paginación al pie */}
        {data.total_pages > 1 && (
          <div style={{
            padding: isMobile ? '12px 16px' : '12px 24px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-raised)',
          }}>
            <Pagination
              page={data.page || 1}
              totalPages={data.total_pages || 1}
              total={data.total || 0}
              limit={data.limit || 100}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function ReasonChip({ reason, count, active, onClick, labelOverride }) {
  const style = reason ? getReasonStyle(reason) : { color: 'var(--accent)', bg: 'var(--accent-soft)', label: labelOverride || 'All' }
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 10px', borderRadius: 6,
        border: `1px solid ${active ? style.color : 'var(--border)'}`,
        background: active ? `${style.color}18` : 'var(--bg-surface)',
        color: active ? style.color : 'var(--text-secondary)',
        fontSize: 11, fontWeight: active ? 700 : 500,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {labelOverride || style.label}
      <span style={{
        fontSize: 10,
        fontFamily: 'ui-monospace, monospace',
        opacity: 0.8,
      }}>
        {count.toLocaleString()}
      </span>
    </button>
  )
}


// ════════════════════════════════════════════════════════════════════════════
//   MODAL · RECHAZOS POR LENDER (dentro del waterfall)
//   Lee de /waterfall/rejected-by-lender. Cada fila es un (lead, lender)
//   donde el lender rechazó. Filtros: chips por lender.
// ════════════════════════════════════════════════════════════════════════════

const PHASE_LABELS = {
  rotation: 'Rotation',
  auction:  'Auction',
}

const RESULT_LABELS = {
  rejected: 'Rejected',
  error:    'Error',
  timeout:  'Timeout',
}

function RejectedByLenderModal({ open, onClose, dateRange, lenders }) {
  const isMobile = useIsMobile()
  const onlyToday = !dateRange || dateRange === 'today'
  const [data, setData] = useState({
    rejections: [], total: 0, total_pages: 1, by_lender: {},
  })
  const [page, setPage] = useState(1)
  const [lenderFilter, setLenderFilter] = useState(null)
  const [loading, setLoading] = useState(false)

  // Cerrar con ESC
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setPage(1)
      setLenderFilter(null)
    }
  }, [open])

  // Fetch
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    const params = { page, limit: 100 }
    if (lenderFilter) params.lender = lenderFilter
    const since = getDateRangeSinceIso(dateRange)
    if (since) params.since = since
    api.get('/waterfall/rejected-by-lender', { params })
      .then(({ data }) => {
        if (!cancelled) setData(data)
      })
      .catch((err) => {
        console.error('[rejected-by-lender] fetch failed:', err)
        if (!cancelled) toast.error('Error loading waterfall rejections')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, page, lenderFilter, dateRange])

  // Reset page al cambiar filtro
  useEffect(() => {
    setPage(1)
  }, [lenderFilter])

  if (!open) return null

  const breakdown = data.by_lender || {}
  const lenderChips = Object.entries(breakdown)
    .sort((a, b) => b[1].count - a[1].count)

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? 12 : 24, zIndex: 1000,
        animation: 'fadeIn 0.15s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? '100%' : '90%', maxWidth: 1200,
          maxHeight: isMobile ? '90vh' : '85vh',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideUp 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? '14px 16px' : '18px 24px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-raised)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Ban size={18} style={{ color: 'var(--red)', flexShrink: 0 }} />
              <h2 style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, margin: 0 }}>
                Rejections by lender in the waterfall
              </h2>
              {onlyToday && (
                <span className="badge badge-blue" style={{ fontSize: 10 }}>
                  Today only
                </span>
              )}
            </div>
            <p style={{
              margin: '4px 0 0 28px', fontSize: 11,
              color: 'var(--text-muted)',
            }}>
              {data.total ?? '—'} rejections · one row per (lead, lender) ·
              read from waterfallLeadFlow
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            style={{ padding: '6px 10px', flexShrink: 0 }}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Filtros por lender */}
        {lenderChips.length > 0 && (
          <div style={{
            padding: isMobile ? '12px 16px' : '12px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 1.2,
              color: 'var(--text-muted)',
            }}>
              FILTRAR POR LENDER
            </span>
            
            <LenderRejectChip
              identifier={null}
              name="All"
              count={lenderChips.reduce((s, [, v]) => s + v.count, 0)}
              active={lenderFilter === null}
              onClick={() => setLenderFilter(null)}
            />
            {/*
            {lenderChips.map(([ident, info]) => (
              <LenderRejectChip
                key={ident}
                identifier={ident}
                name={info.name}
                count={info.count}
                active={lenderFilter === ident}
                onClick={() => setLenderFilter(ident)}
              />
            ))}*/}
          </div>
        )}
          

        {/* Tabla / tarjetas con scroll */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && data.rejections.length === 0 ? (
            <div style={{
              padding: 60, textAlign: 'center', color: 'var(--text-muted)',
            }}>
              <RefreshCw size={24} style={{ animation: 'spin 1.4s linear infinite' }} />
              <p style={{ marginTop: 12, fontSize: 13 }}>Loading…</p>
            </div>
          ) : data.rejections.length === 0 ? (
            <div style={{
              padding: 60, textAlign: 'center', color: 'var(--text-muted)',
            }}>
              <Ban size={28} style={{ opacity: 0.4 }} />
              <p style={{ marginTop: 12, fontSize: 13 }}>
                No rejections with this filter.
              </p>
            </div>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14 }}>
              {data.rejections.map((r) => (
                <div key={r._id} className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={{
                    fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600,
                    color: 'var(--text-primary)', wordBreak: 'break-all',
                  }}>
                    {r.kickoff_lead_id || '—'}
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LenderBadge identifier={r.lender_identifier} size={22} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>
                        {r.lender_name || r.lender_identifier}
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>
                        {PHASE_LABELS[r.phase] || r.phase}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      display: 'inline-block',
                      fontSize: 10, fontWeight: 700,
                      padding: '2px 7px', borderRadius: 4,
                      background: r.result === 'rejected' ? 'var(--red-bg)'
                        : r.result === 'timeout' ? 'var(--amber-bg)'
                        : 'var(--red-bg)',
                      color: r.result === 'rejected' ? 'var(--red)'
                        : r.result === 'timeout' ? 'var(--amber)'
                        : 'var(--red)',
                      fontFamily: 'ui-monospace, monospace',
                    }} title={r.reason || r.result}>
                      {r.reason || RESULT_LABELS[r.result] || r.result}
                    </span>
                    <HttpBadge code={r.lender_http_status ?? r.http_status_code} />
                  </div>

                  {r.detail && (
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {r.detail}
                      {r.elapsed_ms != null && (
                        <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>
                          {fmtMs(r.elapsed_ms)}
                        </span>
                      )}
                    </span>
                  )}

                  <span style={{
                    fontSize: 10, color: 'var(--text-muted)',
                    fontFamily: 'ui-monospace, monospace',
                  }} title={fmtDateTime(r.createdAt)}>
                    {fmtRelative(r.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '300px 160px 110px 1fr 130px',
                gap: 12,
                padding: '10px 24px',
                borderBottom: '1px solid var(--border)',
                fontSize: 9, color: 'var(--text-muted)',
                letterSpacing: 1.2, fontWeight: 700,
                background: 'var(--bg-raised)',
                position: 'sticky', top: 0, zIndex: 1,
                fontFamily: 'ui-monospace, monospace',
              }}>
                <span>KICKOFF LEAD ID</span>
                <span>WHO REJECTED</span>
                <span>REASON</span>
                <span>DETAIL</span>
                <span>DATE</span>
              </div>
              {data.rejections.map((r) => {
                const lenderStyle = getLenderStyle(r.lender_identifier)
                return (
                  <div key={r._id} style={{
                    display: 'grid',
                    gridTemplateColumns: '300px 160px 110px 1fr 130px',
                    gap: 12,
                    padding: '12px 24px',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 11, alignItems: 'center',
                  }}>
                    {/* Lead ID */}
                    <span style={{
                      fontFamily: 'ui-monospace, monospace',
                      fontWeight: 600, color: 'var(--text-primary)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={r.kickoff_lead_id}>
                      {r.kickoff_lead_id || '—'}
                    </span>

                    {/* Quién rechazó */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <LenderBadge identifier={r.lender_identifier} size={22} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontSize: 11, fontWeight: 600,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {r.lender_name || r.lender_identifier}
                        </div>
                        <div style={{
                          fontSize: 9, color: 'var(--text-muted)',
                          fontFamily: 'ui-monospace, monospace',
                        }}>
                          {PHASE_LABELS[r.phase] || r.phase}
                        </div>
                      </div>
                    </div>

                    {/* Razón = result + HTTP */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{
                        display: 'inline-block',
                        fontSize: 10, fontWeight: 700,
                        padding: '2px 7px', borderRadius: 4,
                        background: r.result === 'rejected' ? 'var(--red-bg)'
                          : r.result === 'timeout' ? 'var(--amber-bg)'
                          : 'var(--red-bg)',
                        color: r.result === 'rejected' ? 'var(--red)'
                          : r.result === 'timeout' ? 'var(--amber)'
                          : 'var(--red)',
                        width: 'fit-content',
                        fontFamily: 'ui-monospace, monospace',
                        maxWidth: '100%',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }} title={r.reason || r.result}>
                        {r.reason || RESULT_LABELS[r.result] || r.result}
                      </span>
                      <HttpBadge code={r.lender_http_status ?? r.http_status_code} />
                    </div>

                    {/* Detalle */}
                    <span style={{
                      color: 'var(--text-secondary)',
                      fontSize: 11,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={r.detail || ''}>
                      {r.detail || '—'}
                      {r.elapsed_ms != null && (
                        <span style={{
                          marginLeft: 8,
                          color: 'var(--text-muted)',
                          fontFamily: 'ui-monospace, monospace', fontSize: 10,
                        }}>
                          {fmtMs(r.elapsed_ms)}
                        </span>
                      )}
                    </span>

                    {/* Fecha */}
                    <span style={{
                      color: 'var(--text-muted)',
                      fontFamily: 'ui-monospace, monospace', fontSize: 10,
                    }} title={fmtDateTime(r.createdAt)}>
                      {fmtRelative(r.createdAt)}
                    </span>
                  </div>
                )
              })}
            </>
          )}
        </div>

        {/* Paginación */}
        {data.total_pages > 1 && (
          <div style={{
            padding: isMobile ? '12px 16px' : '12px 24px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-raised)',
          }}>
            <Pagination
              page={data.page || 1}
              totalPages={data.total_pages || 1}
              total={data.total || 0}
              limit={data.limit || 100}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function LenderRejectChip({ identifier, name, count, active, onClick }) {
  const style = identifier
    ? getLenderStyle(identifier)
    : { color: 'var(--accent)', bg: 'var(--accent-soft)', letter: '·' }
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '5px 10px 5px 6px', borderRadius: 6,
        border: `1px solid ${active ? style.color : 'var(--border)'}`,
        background: active ? `${style.color}18` : 'var(--bg-surface)',
        color: active ? style.color : 'var(--text-secondary)',
        fontSize: 11, fontWeight: active ? 700 : 500,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {identifier && <LenderBadge identifier={identifier} size={16} />}
      {name}
      <span style={{
        fontSize: 10,
        fontFamily: 'ui-monospace, monospace',
        opacity: 0.8,
      }}>
        {count.toLocaleString()}
      </span>
    </button>
  )
}


// ════════════════════════════════════════════════════════════════════════════
//   PÁGINA PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
//   PÁGINA PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════
export default function WaterfallValidatorPage() {
  const isMobile = useIsMobile()

  // Estado del waterfall
  const [leadsResp, setLeadsResp] = useState({
    leads: [], total: 0, page: 1, total_pages: 1, limit: PAGE_SIZE,
  })
  const [selectedId, setSelectedId] = useState(null)
  const [rotationStats, setRotationStats] = useState(null)
  const [health, setHealth] = useState(null)
  const [lenders, setLenders] = useState([])
  const [summary, setSummary] = useState(null)
  const [rejectedModalOpen, setRejectedModalOpen] = useState(false)
  const [rejectedByLenderModalOpen, setRejectedByLenderModalOpen] = useState(false)
  // En móvil el detalle del lead se abre en un sheet deslizable.
  const [detailOpenMobile, setDetailOpenMobile] = useState(false)

  // Date Range filter — defaults to 'today'
  const [dateRange, setDateRange] = useState('today')

  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [sourceIdentifiers, setSourceIdentifiers] = useState([])
  const [paused, setPaused] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Auto-pausa cuando navegamos a página != 1 (no perdemos contexto del lead viejo)
  const userTogglesRef = useRef(false)
  useEffect(() => {
    if (userTogglesRef.current) {
      // Si el usuario tocó pausa/play manualmente, no auto-pausamos por paginación
      return
    }
    if (page > 1 && !paused) {
      setPaused(true)
    }
  }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset a página 1 cuando cambian los filtros
  const filtersKey = JSON.stringify(filters)
  useEffect(() => {
    setPage(1)
  }, [filtersKey])

  // Refs para timers
  const leadsTimerRef = useRef(null)
  const statsTimerRef = useRef(null)
  const healthTimerRef = useRef(null)
  const summaryTimerRef = useRef(null)

  // Construir params para /waterfall/leads aplicando regla de filtros
  const buildQueryParams = useCallback(() => {
    const p = { limit: PAGE_SIZE, page }
    if (filters.state)   p.states = filters.state
    if (filters.is_test === true) p.is_test = true
    if (filters.source_identifier) p.source_identifier = filters.source_identifier
    const lenderParam = resolveLenderParam(filters.state, filters.lender)
    if (lenderParam) {
      p[lenderParam.key] = lenderParam.value
    }
    const since = getDateRangeSinceIso(dateRange)
    if (since) p.since = since
    return p
  }, [filters, page, dateRange])


  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      const { data } = await api.get('/waterfall/leads', {
        params: buildQueryParams(),
      })
      setLeadsResp({
        leads: data.leads || [],
        total: data.total ?? (data.leads?.length || 0),
        page: data.page || 1,
        total_pages: data.total_pages || 1,
        limit: data.limit || PAGE_SIZE,
      })
      setError(null)
      setSelectedId((prev) => {
        if (prev && data.leads?.some((l) => l.kickoff_lead_id === prev)) return prev
        return data.leads?.[0]?.kickoff_lead_id || null
      })
    } catch (err) {
      console.error('[waterfall] fetchLeads failed:', err)
      const rawDetail = err.response?.data?.detail || err.response?.data?.message || err.message
      const errorMsg = typeof rawDetail === 'string' && rawDetail.includes('<html') 
        ? `Server Error (${err.response?.status || 500}). Verify backend connectivity or logs.`
        : (typeof rawDetail === 'string' ? rawDetail : JSON.stringify(rawDetail))
      setError(errorMsg)
      if (err.response?.status !== 401) {
        toast.error(`Error loading waterfall leads (${err.response?.status || 500})`)
      }
    } finally {
      setLoading(false)
    }
  }, [buildQueryParams])

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/waterfall/rotation/stats', {
        params: { window_minutes: 60 },
      })
      setRotationStats(data)
    } catch (err) {
      console.error('[waterfall] fetchStats failed:', err)
    }
  }, [])

  const fetchHealth = useCallback(async () => {
    try {
      const { data } = await api.get('/waterfall/health')
      setHealth(data)
    } catch (err) {
      console.error('[waterfall] fetchHealth failed:', err)
      setHealth({ status: 'error' })
    }
  }, [])

  const fetchLenders = useCallback(async () => {
    try {
      const { data } = await api.get('/waterfall/lenders')
      setLenders(data.lenders || [])
    } catch (err) {
      console.error('[waterfall] fetchLenders failed:', err)
      setLenders([])
    }
  }, [])

  const fetchSourceIdentifiers = useCallback(async () => {
    try {
      const { data } = await api.get('/waterfall/source-identifiers')
      setSourceIdentifiers(data.identifiers || [])
    } catch (err) {
      console.error('[waterfall] fetchSourceIdentifiers failed:', err)
      setSourceIdentifiers([])
    }
  }, [])

  const fetchSummary = useCallback(async () => {
    try {
      const params = {}
      const since = getDateRangeSinceIso(dateRange)
      if (since) params.since = since
      if (filters.source_identifier) params.source_identifier = filters.source_identifier
      const { data } = await api.get('/waterfall/summary', { params })
      setSummary(data)
    } catch (err) {
      console.error('[waterfall] fetchSummary failed:', err)
    }
  }, [dateRange, filters.source_identifier])

  // Carga inicial (cualquier vista)
  useEffect(() => {
    fetchLenders()
    fetchSourceIdentifiers()
  }, [fetchLenders])

  // Initial fetch
  useEffect(() => {
    fetchLeads()
    fetchStats()
    fetchHealth()
    fetchSummary()
  }, [fetchLeads, fetchStats, fetchHealth, fetchSummary])

  // Polling interval
  useEffect(() => {
    if (paused) return

    leadsTimerRef.current   = setInterval(fetchLeads,   POLL_LEADS_MS)
    statsTimerRef.current   = setInterval(fetchStats,   POLL_STATS_MS)
    healthTimerRef.current  = setInterval(fetchHealth,  POLL_HEALTH_MS)
    summaryTimerRef.current = setInterval(fetchSummary, POLL_SUMMARY_MS)

    return () => {
      clearInterval(leadsTimerRef.current)
      clearInterval(statsTimerRef.current)
      clearInterval(healthTimerRef.current)
      clearInterval(summaryTimerRef.current)
    }
  }, [paused, fetchLeads, fetchStats, fetchHealth, fetchSummary])

  const selectedLead = useMemo(
    () => leadsResp.leads.find((l) => l.kickoff_lead_id === selectedId) || null,
    [leadsResp.leads, selectedId],
  )

  // En móvil, seleccionar un lead también abre el sheet de detalle.
  const handleSelectLead = useCallback((id) => {
    setSelectedId(id)
    if (isMobile) setDetailOpenMobile(true)
  }, [isMobile])

  const handleManualRefresh = () => {
    setLoading(true)
    fetchLeads()
    fetchStats()
    fetchHealth()
    fetchLenders()
    fetchSummary()
    toast.success('Datos actualizados')
  }

  const handleTogglePause = () => {
    userTogglesRef.current = true  // marcamos que el usuario decidió manualmente
    setPaused((p) => !p)
  }

  const handlePageChange = (newPage) => {
    userTogglesRef.current = false  // permite que la auto-pausa vuelva a aplicar
    setPage(newPage)
    // Si vuelve a página 1, despausamos automáticamente
    if (newPage === 1 && paused) {
      setPaused(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Animaciones */}
      <style>{`
        @keyframes pulse-dot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.3); opacity: 0.7; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
      }}>
        <div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: isMobile ? 22 : 26, fontWeight: 700, margin: 0,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <Zap size={22} style={{ color: 'var(--accent)' }} />
            Waterfall Validator
          </h1>
          <p style={{
            margin: '4px 0 0 34px', fontSize: 12,
            color: 'var(--text-muted)',
          }}>
            Real-time verification of the rotating counter, the exclusive rotation and the auction
          </p>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          flexWrap: 'wrap',
          width: isMobile ? '100%' : undefined,
        }}>
          {/* Date Range Selector (Defaults to Today) */}
          <DateRangeSelector value={dateRange} onChange={setDateRange} />

          {health && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 6,
              background: health.status === 'ok' ? 'var(--green-bg)'
                : health.status === 'no_data' ? 'var(--bg-raised)'
                : 'var(--amber-bg)',
              border: `1px solid ${
                health.status === 'ok' ? 'var(--green)'
                  : health.status === 'no_data' ? 'var(--border)'
                  : 'var(--amber)'
              }30`,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: health.status === 'ok' ? 'var(--green)'
                  : health.status === 'no_data' ? 'var(--text-muted)'
                  : 'var(--amber)',
                animation: health.status === 'ok' && !paused ? 'pulse-dot 2s infinite' : 'none',
              }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>
                {health.status === 'ok'      ? 'Tracker active'
                : health.status === 'no_data' ? 'No data yet'
                : health.status === 'stale'  ? `Last lead ${health.last_lead_seconds_ago}s ago`
                                              : 'Tracker degraded'}
              </span>
            </div>
          )}
          {paused && (
            <span className="badge badge-amber" style={{ fontSize: 10 }}>
              ⏸ {page > 1 ? 'Paused by pagination' : 'Polling paused'}
            </span>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 'var(--radius)',
          background: 'var(--red-bg)',
          border: '1px solid var(--red)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <AlertCircle size={16} style={{ color: 'var(--red)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--red)' }}>
              Error consultando el backend
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {String(error)}. Verify backend connectivity.
            </div>
          </div>
        </div>
      )}

      {/* Waterfall Content */}
      <>
        {/* Panel de lenders */}
          <LendersPanel
            lenders={lenders}
            distribution={rotationStats?.distribution}
          />

          {/* Cockpit */}
          <RotationCockpit stats={rotationStats} />

          {/* Stats strip */}
          <StatsStrip
            summary={summary}
            onOpenRejected={() => setRejectedModalOpen(true)}
            onOpenRejectedByLender={() => setRejectedByLenderModalOpen(true)}
            dateRange={dateRange}
          />

          {/* Filter bar */}
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            onRefresh={handleManualRefresh}
            isPaused={paused}
            onTogglePause={handleTogglePause}
            leadCount={leadsResp.leads.length}
            totalCount={leadsResp.total}
            lenders={lenders}
            sourceIdentifiers={sourceIdentifiers}
          />

          {/* Tabla + detalle */}
          {isMobile ? (
            <LeadsTable
              leads={leadsResp.leads}
              selectedId={selectedId}
              onSelect={handleSelectLead}
            />
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)',
              gap: 16,
              alignItems: 'start',
            }}>
              <LeadsTable
                leads={leadsResp.leads}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
              <LeadDetailPanel lead={selectedLead} />
            </div>
          )}

          {/* Paginación (Punto 4) */}
          <Pagination
            page={leadsResp.page}
            totalPages={leadsResp.total_pages}
            total={leadsResp.total}
            limit={leadsResp.limit}
            onPageChange={handlePageChange}
          />

          {/* Footer leyenda HTTP */}
          <div style={{
            padding: '10px 16px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            display: 'flex', alignItems: 'center', gap: 20,
            flexWrap: 'wrap', rowGap: 6,
            fontSize: 10, fontFamily: 'ui-monospace, monospace',
            color: 'var(--text-muted)',
          }}>
            <span style={{ fontWeight: 700, letterSpacing: 0.8 }}>HTTP CODES</span>
            <span style={{ color: 'var(--green)' }}>● 200 accepted / won</span>
            <span>·</span>
            <span style={{ color: 'var(--amber)' }}>● 408 timeout</span>
            <span>·</span>
            <span style={{ color: 'var(--red)' }}>● 422 rejected (did not qualify)</span>
            <span>·</span>
            <span style={{ color: 'var(--red)' }}>● 500 lender error</span>
            {!isMobile && <span style={{ flex: 1 }} />}
            <span>polling every {POLL_LEADS_MS / 1000}s · {PAGE_SIZE} per page</span>
          </div>
        </>

      {/* Detalle del lead en sheet (solo móvil) */}
      {isMobile && detailOpenMobile && selectedLead && (
        <MobileDetailSheet onClose={() => setDetailOpenMobile(false)}>
          <LeadDetailPanel lead={selectedLead} />
        </MobileDetailSheet>
      )}

      {/* Modal de leads rechazados (compartido — se abre desde el StatsStrip) */}
      <RejectedLeadsModal
        open={rejectedModalOpen}
        onClose={() => setRejectedModalOpen(false)}
        summary={summary}
        dateRange={dateRange}
      />

      {/* Modal de rechazos POR LENDER del waterfall */}
      <RejectedByLenderModal
        open={rejectedByLenderModalOpen}
        onClose={() => setRejectedByLenderModalOpen(false)}
        dateRange={dateRange}
        lenders={lenders}
      />
    </div>
  )
}
